import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade } from 'swiper/modules';
import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { Swiper as SwiperClass } from 'swiper/types';
import isObjectLike from 'lodash/isObjectLike';
import SliderItem from '@components/Slider/SliderItem';
import styles from '@features/homepage/sections/Brand/components/BrandSlider/BrandSlider.styles';
import SliderControlArrow from '@components/Slider/SliderArrowControl';
import SliderContent from '@components/Slider/SliderContent';
import { ISliderItems } from '@components/typescript';
import useResponsive from '@features/responsive/hooks/useResponsive';
import SliderIndicator from '@components/Slider/SliderIndicator';
import prepareURLByCMSLink from '@utils/prepareURLByCMSLink';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
type PropsType = {
  slides: ISliderItems;
  isFirst: boolean;
  firstShow: boolean;
  setFirstShow: Dispatch<SetStateAction<boolean>>;
};
const BrandSlider = ({
  slides,
  isFirst,
  firstShow,
  setFirstShow
}: PropsType) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperClass>();
  const [titleHidden, setTitleHidden] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const {
    isNarrowerThanMobilelg,
    isNarrowerThanTabletlg
  } = useResponsive();
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isSliderControlArrowVisible, setSliderControlArrowVisible] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const onNextClick = () => {
    if (firstShow) setFirstShow(false);
    if (!swiperInstance) return;
    swiperInstance.slideNext();
  };
  const onPrevClick = () => {
    if (firstShow) setFirstShow(false);
    if (!swiperInstance) return;
    swiperInstance.slidePrev();
  };
  const onSlideChange = swiper => {
    if (firstShow) setFirstShow(false);
    const {
      realIndex
    } = swiper;
    setCurrentSlide(realIndex + 1);
  };
  const renderSliderTitle = (slide, index: number) => {
    const {
      link,
      buttonText,
      buttonStyle,
      content: {
        titleColor,
        descriptionColor,
        topTextColor
      }
    } = slide;
    return titleHidden && <SliderContent title="" titleColor={titleColor} description="" descriptionColor={descriptionColor} topText="" topTextColor={topTextColor} href={isObjectLike(link) && link.path ? prepareURLByCMSLink(link) : link} position={{
      horizontal: 'center',
      vertical: 'center'
    }} buttonHorizontalPosition="center" buttonVerticalPosition="end" buttonText={buttonText} buttonStyle={buttonStyle} isFirst={isFirst && !index} data-testid={`brand-slider-content-${index}`} />;
  };
  useEffect(() => {
    if (isMounted) {
      setTitleHidden(!!isNarrowerThanMobilelg);
    }
  }, [isNarrowerThanMobilelg, isMounted]);
  const onIndicatorClick = (indicatorIndex: number) => {
    if (firstShow) setFirstShow(false);
    swiperInstance?.slideTo(indicatorIndex);
  };

  // WCAG 2.1.1 Keyboard - automatyczne przełączanie slajdu gdy focus trafia na element na innym slajdzie
  useEffect(() => {
    if (!swiperInstance || !isMounted) return;
    const handleFocusIn = (event: FocusEvent) => {
      const focusedElement = event.target as HTMLElement;
      if (!focusedElement) return;

      // Sprawdź czy element jest w kontenerze slidera
      const sliderContainer = swiperInstance.el;
      if (!sliderContainer.contains(focusedElement)) return;

      // Znajdź slajd zawierający element z focusem
      const slideContainingFocus = focusedElement.closest('.swiper-slide') as HTMLElement;
      if (!slideContainingFocus) return;

      // Sprawdź czy slajd jest aktywny
      const isActive = slideContainingFocus.classList.contains('swiper-slide-active');
      if (isActive) return;

      // Znajdź indeks slajdu
      const allSlides = Array.from(sliderContainer.querySelectorAll('.swiper-slide'));
      const slideIndex = allSlides.indexOf(slideContainingFocus);
      if (slideIndex === -1) return;

      // Przełącz slider na slajd z focusem
      // Używamy realIndex dla pętli (loop)
      const realIndex = swiperInstance.slides.indexOf(slideContainingFocus);
      if (realIndex !== -1) {
        swiperInstance.slideTo(realIndex);
        if (firstShow) setFirstShow(false);
      }
    };
    const sliderContainer = swiperInstance.el;
    sliderContainer.addEventListener('focusin', handleFocusIn);
    return () => {
      sliderContainer.removeEventListener('focusin', handleFocusIn);
    };
  }, [swiperInstance, isMounted, firstShow, setFirstShow]);

  // NOTE: Temporary solution to avoid server side rendering caused by useResponsive hook
  if (!isMounted) {
    return null;
  }
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: mouse hover for UI controls
    <header role="presentation" sx={styles.brandSliderContainer} onMouseEnter={() => !isNarrowerThanTabletlg && setSliderControlArrowVisible(true)} onMouseLeave={() => !isNarrowerThanTabletlg && setSliderControlArrowVisible(false)} data-testid="brand-slider">
      <Swiper modules={[EffectFade]} effect="fade" onSwiper={swiper => setSwiperInstance(swiper)} centeredSlides loop onActiveIndexChange={onSlideChange} fadeEffect={{
        crossFade: true
      }} data-testid="brand-slider-swiper">
        {slides.map((slide, index) => <SwiperSlide key={slide.itemId}>
            {renderSliderTitle(slide, index)}
            <SliderItem item={slide} titleHidden={titleHidden} isActive={currentSlide === index + 1} isDividedOnMobile={titleHidden} isFirst={isFirst && !index} data-testid={`brand-slider-item-${index}`} />
            {slides.length > 1 && <SliderIndicator slidesLength={slides.length} current={currentSlide} onClick={onIndicatorClick} />}
          </SwiperSlide>)}
        {isSliderControlArrowVisible && slides.length > 1 && <div sx={styles.brandSliderControlArrowsContainer}>
            <SliderControlArrow direction="left" callback={onPrevClick} />
            <SliderControlArrow direction="right" callback={onNextClick} />
          </div>}
      </Swiper>
    </header>
  );
};
export default BrandSlider;